// base
import { createAsyncThunk } from '@reduxjs/toolkit';

// api
import { authApi } from '../apis/auth.api';

// action
import { authActionV2 } from '../actions/auth.actionV2';

// service
import { COOKIE_ACCESS_TOKEN, COOKIE_REFRESH_TOKEN, cookieStorage } from 'services/cookie';

// modules
import { userApi } from 'modules/user/apis/user.api';
import { commonActions, ResultError, MenuListProps } from 'modules/common';
import type {
  ReqChangePassword,
  ReqSignIn,
  ReqSms,
  ResChangePassword,
  ResSignInThunk,
  ResSms
} from '../models/auth.model';

// enums
import { basicErrorForm } from 'enums';

/** 로그인 thunk 입니다. */
export const userLoginV2 = createAsyncThunk<
  ResSignInThunk,
  ReqSignIn,
  {
    rejectValue: ResultError;
  }
>(authActionV2.POST_SIGN_IN, async (data, { dispatch, fulfillWithValue, rejectWithValue }) => {
  try {
    cookieStorage.removeCookie(COOKIE_ACCESS_TOKEN);
    cookieStorage.removeCookie(COOKIE_REFRESH_TOKEN);

    const res = await authApi.postSignIn(data);

    const { ACCESS_TOKEN, REFRESH_TOKEN, userId, temporaryPasswdStatus } = res;

    if (!ACCESS_TOKEN) {
      console.error('Available after administrator sign-up approval.');

      return rejectWithValue({
        ...basicErrorForm,
        message: 'Available after administrator sign-up approval.'
      });
    }

    cookieStorage.setCookie(COOKIE_ACCESS_TOKEN, ACCESS_TOKEN);
    cookieStorage.setCookie(COOKIE_REFRESH_TOKEN, REFRESH_TOKEN);

    const resultUser = {
      ...res,
      isLogin: true,
      temporaryPasswdStatus,
      userId
    };

    const user = await userApi.getUsersId(userId);
    const allMenuList = await fetch('/assets/menuList.json').then((res) => res.json());

    const findMenu = allMenuList.menuList.find((menu: MenuListProps) =>
      menu.role.some((rol) => rol === user.resultObject.role)
    );

    dispatch(commonActions.setMenuList(findMenu?.menus || []));

    return fulfillWithValue({
      ...resultUser,
      userInfo: {
        ...user.resultObject,
        thumnails: undefined,
        accountType: user.resultObject.role.replace('ROLE_', '')
      }
    });
  } catch (error) {
    const err = error as ResultError;

    return rejectWithValue(err);
  }
});

/** SMS 문자발송 thunk 입니다 */
export const getSmsV2 = createAsyncThunk<
  ResSms,
  ReqSms,
  {
    rejectValue: ResultError;
  }
>(authActionV2.GET_SMS, async (data, { fulfillWithValue, rejectWithValue }) => {
  try {
    const res = await authApi.getSms(data);

    return fulfillWithValue(res);
  } catch (error) {
    const err = error as ResultError;

    return rejectWithValue(err);
  }
});

/** 비밀번호 변경 thunk 입니다. */
export const postPasswordV2 = createAsyncThunk<
  ResChangePassword,
  ReqChangePassword,
  {
    rejectValue: ResultError;
  }
>(authActionV2.POST_PASSWORD_CHANGE, async (data, { fulfillWithValue, rejectWithValue }) => {
  try {
    const res = await authApi.postPassword(data);

    return fulfillWithValue(res);
  } catch (error) {
    const err = error as ResultError;

    return rejectWithValue(err);
  }
});
